// @givmo/board — a meeting's calendar on its Overview: the Invitations line and the dialogs behind it
// (CBG-CAL1 slice 1d; the approved frames `meeting-sent`, `-not-sent`, `-attention`,
// `-organizer-left`, `-restored`, `-from-givmo`, `-detached`, `dialog-fix-*`,
// `dialog-send-from-another`, `dialog-move-meetings`).
//
// ONE line in the card that already says where and when, read off the meeting read's `calendar`
// block, and ONE act at its end: "Change", "Fix", "Move meetings", "Schedule it again", "Send
// invitations". The one line with two is the old invitation Board can't prove is gone: "Send the
// cancellation", then "I've told everyone" (question 6, CBG-FIX32: the officer's own remedy,
// `POST …/invitations/directors-told`, which sends nothing and is offered in any meeting state, so a
// warning nothing else clears can end). The line says what holds NOW (the served count is the live
// invitees; `sent_at` is the last write the provider took, not the first send), so it names no date;
// the History card says what happened when. Under a sent line, at most two quiet lines say when it
// may not be the calendar's latest (CBG-FIX36 P1, `quietLines`). Amber only where someone must act.
// A reader without `board_meeting:write` sees the fact alone. Every act is one the backend serves
// (contract §8); every refusal reads as the backend says it, and a failure that isn't the backend's
// reads as the shipped fixed line (B8).
//
// DARK until served: the block is null while both calendar flags are off, and "Not sent · Send
// invitations" shows only where the senders read is served and offers a sender.

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { BoardCalendarSendersOut, OrgBoardMeetingOut } from '@givmo/api-client';
import {
  Button,
  Dialog,
  ErrorMessage,
  RadioTile,
  RadioTileGroup,
  toast,
} from '@givmo/design-system';
import { hasBoardPermission, useBoardHost } from '../../board-host';
import { useBoardOperations } from '../../board-runtime';
import { useNavigate } from '../../board-routing';
import { BOARD_PAGES } from '../../board-routes';
import {
  meetingsSentFrom,
  useMeetingsById,
  useOfficerDirectory,
  meetingsNamed,
} from '../../calendar/calendar-model';
import {
  useBoardCalendarConnections,
  useCalendarSenders,
  useOrgConnections,
  useRefreshCalendar,
  type CalendarConnection,
} from '../../calendar/calendar-queries';
import { calendarWords as w, providerName } from '../../calendar/calendar-words';
import { useCalendarConnect } from '../../calendar/use-calendar-connect';
import { decodeMeetingCalendar, type MeetingCalendar } from '../meetings/calendar-decode';
import { CancelMeetingDialog } from '../meetings/meeting-frame';
import { MoveMeetingsDialog, failureLine } from './account-dialogs';
import { CALENDAR_MANAGE_PERMISSION } from './board-calendar-card';
import { ACT, BODY, DIALOG_TITLE, RECONNECT_CODES, SMALL, attentionLine } from './calendar-parts';
import { ConnectAnswer } from './connect-dialog';

/** The send choice that re-sends through this meeting's own sender, as this viewer may name it. */
export type LinkSender = 'my_calendar' | 'board_account' | 'givmo_email';

/**
 * The quiet lines under the sent line (CBG-FIX36 P1): the two served facts that say the line may not
 * be the calendar's latest. `check_failed` (the calendar channel only: the page's re-read of the
 * calendar didn't finish inside its budget, so this is Board's stored record), then `out_of_step` (a
 * change made in Board hasn't reached the calendar or the mail yet). Never under an attention line,
 * which already says what's wrong, nor a detached one, which Board no longer keeps in step. No act
 * and no colour: opening the meeting reads and sends again by itself.
 *
 * `out_of_step` is said only while the meeting takes invitations (`live`). Board sends a change only
 * to a meeting that is scheduled and ahead, so once it has started or been cancelled its mark never
 * clears, and "Board tries again when this meeting is next opened" would never come true.
 *
 * One attention line has a quiet line of its own (CBG-FIX181): a meeting moved to another calendar
 * says, in every meeting state, that Board can no longer change or cancel it there, since cancelling
 * it in Board leaves it standing in every invited calendar.
 */
function quietLines(block: MeetingCalendar, live: boolean): { words: string; testId: string }[] {
  if (block.state === 'needs_attention' && block.needs_attention_code === 'marker_missing') {
    return [{ words: w.movedNote, testId: 'meeting-invitations-moved' }];
  }
  if (block.state !== 'pending' && block.state !== 'sent' && block.state !== 'cancelled') return [];
  const provider = providerName(block.provider);
  return [
    ...(block.check_failed && block.channel === 'org_calendar'
      ? [{ words: w.checkFailed(provider), testId: 'meeting-invitations-check-failed' }]
      : []),
    ...(block.out_of_step && live
      ? [
          {
            words: block.channel === 'givmo_invitation' ? w.outOfStepGivmo : w.outOfStep(provider),
            testId: 'meeting-invitations-out-of-step',
          },
        ]
      : []),
  ];
}

/** What the meeting page needs to know about the meeting's calendar, from this viewer's side. */
export interface MeetingCalendarView {
  block: MeetingCalendar | null;
  senders: BoardCalendarSendersOut | null;
  /** The viewer's own connection, when it is the one organizing this meeting. */
  own: CalendarConnection | undefined;
  /** The send choice that re-sends through the same sender, or null when this viewer has none. */
  linkSender: LinkSender | null;
}

/** Which sender a re-send names: the Givmo channel, or the viewer's sender that IS the organizer. */
export function linkSenderFor(
  block: MeetingCalendar,
  senders: BoardCalendarSendersOut | null,
): LinkSender | null {
  if (block.channel === 'givmo_invitation') return 'givmo_email';
  const organizer = (block.organizer_email ?? '').trim().toLowerCase();
  if (organizer.length === 0) return null;
  for (const sender of senders?.senders ?? []) {
    if (
      (sender.send_from === 'my_calendar' || sender.send_from === 'board_account') &&
      (sender.organizer_email ?? '').trim().toLowerCase() === organizer
    ) {
      return sender.send_from;
    }
  }
  return null;
}

/** Whether a meeting still takes invitations: scheduled, and its start strictly ahead. */
export function takesInvitations(
  meeting: Pick<OrgBoardMeetingOut, 'status' | 'starts_at'>,
  nowMs: number = Date.now(),
): boolean {
  if (meeting.status !== 'scheduled' || !meeting.starts_at) return false;
  const start = Date.parse(meeting.starts_at);
  return !Number.isNaN(start) && start > nowMs;
}

/** Read what the meeting's line and acts need. */
export function useMeetingCalendarView(meeting: OrgBoardMeetingOut): MeetingCalendarView {
  const { orgRef, userId } = useBoardHost();
  const block = decodeMeetingCalendar((meeting as { calendar?: unknown }).calendar);
  const senders = useCalendarSenders(orgRef);
  const connections = useOrgConnections(orgRef, block !== null && block.channel === 'org_calendar');
  const organizer = (block?.organizer_email ?? '').trim().toLowerCase();
  const own =
    userId === null || userId === undefined || organizer.length === 0
      ? undefined
      : (connections.data ?? []).find(
          (row) =>
            row.owner_user_id === userId &&
            row.status !== 'disconnected' &&
            row.account_email.trim().toLowerCase() === organizer,
        );
  return {
    block,
    senders: senders.data ?? null,
    own,
    linkSender: block === null ? null : linkSenderFor(block, senders.data ?? null),
  };
}

type Open = 'fix' | 'send-from-another' | 'move' | 'cancel' | null;

/** The line's words with its act: the last word and the act never part (a wrapped act moves with it). */
function WithAct({
  words,
  act,
  then = null,
  attention,
}: {
  words: string;
  act: React.ReactNode;
  /** A second act, free to wrap to its own line (after a zero-width break, so it starts flush). */
  then?: React.ReactNode;
  attention?: boolean;
}) {
  const className = attention ? ACT : undefined;
  if (act === null) return <span className={className}>{words}</span>;
  const cut = words.lastIndexOf(' ');
  const head = cut > 0 ? words.slice(0, cut + 1) : '';
  const last = cut > 0 ? words.slice(cut + 1) : words;
  return (
    <>
      <span className={className}>{head}</span>
      <span className="whitespace-nowrap">
        <span className={className}>{last}</span>
        {act}
      </span>
      {then !== null ? (
        <>
          {'\u200B'}
          {then}
        </>
      ) : null}
    </>
  );
}

function Act({
  children,
  onClick,
  loading,
  disabled,
  testId,
  spacing = 'ml-8',
}: {
  children: string;
  onClick: () => void;
  loading?: boolean;
  /** The line's other act is pending (CBG-FIX37): one act at a time. */
  disabled?: boolean;
  testId: string;
  /** The gap: before the act, or (for a first act a second may wrap after) on both sides of it. */
  spacing?: 'ml-8' | 'mx-8' | 'none';
}) {
  return (
    <Button
      appearance="saas"
      tone="link"
      size="md"
      className={spacing === 'none' ? 'align-baseline' : `${spacing} align-baseline`}
      loading={loading}
      disabled={disabled}
      onClick={onClick}
      testId={testId}
    >
      {children}
    </Button>
  );
}

/**
 * The Invitations line and its dialogs, or null when the meeting shows no line: nothing sent and no
 * channel to send through (or a meeting that no longer takes invitations), or an unknown state.
 */
export function MeetingInvitationsLine({
  meeting,
  view,
  onCancelled,
  row,
}: {
  meeting: OrgBoardMeetingOut;
  view: MeetingCalendarView;
  onCancelled: () => void;
  /** The card's own row around the line (the page's "Invitations" label). */
  row: (line: React.ReactNode) => React.ReactNode;
}) {
  const { orgRef, permissions, orgName } = useBoardHost();
  const operations = useBoardOperations();
  const navigate = useNavigate();
  const refresh = useRefreshCalendar(orgRef);
  const canWrite = hasBoardPermission(permissions, 'board_meeting:write');
  const canManage = hasBoardPermission(permissions, CALENDAR_MANAGE_PERMISSION);
  const [open, setOpen] = useState<Open>(null);
  const flow = useCalendarConnect(orgRef);
  const { block, senders, own, linkSender } = view;
  const live = takesInvitations(meeting);
  const name = orgName ?? '';

  // The departed organizer's connection and meetings: Board's own list for an Owner; for anyone
  // else, the meetings list's blocks naming the same address, moved one by one.
  const organizerLeft = block?.needs_attention_code === 'organizer_left';
  const boardConnections = useBoardCalendarConnections(orgRef, organizerLeft && canManage);
  const meetings = useMeetingsById(orgRef, organizerLeft);
  const directory = useOfficerDirectory(orgRef, organizerLeft);

  const send = useMutation({
    mutationFn: async (sendFrom: LinkSender) => {
      const sender = (senders?.senders ?? []).find((row) => row.send_from === sendFrom);
      await operations.officerSendBoardMeetingInvitations({
        path: { org_ref: orgRef ?? '', meeting_id: meeting.id },
        body: {
          send_from: sendFrom,
          ...(sender?.connection_id ? { connection_id: sender.connection_id } : {}),
        },
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      setOpen(null);
      await refresh(meeting.id);
    },
    onError: (error) => toast.error(failureLine(error)),
  });
  const again = useMutation({
    mutationFn: async () => {
      const { data } = await operations.officerScheduleRestoredBoardMeeting({
        path: { org_ref: orgRef ?? '', meeting_id: meeting.id },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: async (created) => {
      await refresh(meeting.id);
      if (created?.id)
        navigate({ to: `${BOARD_PAGES.meetings}/$meetingId`, params: { meetingId: created.id } });
    },
    onError: (error) => toast.error(failureLine(error)),
  });
  // CBG-FIX181: a meeting moved to another calendar is let go ("Stop updating it", D-CBG-50), never
  // sent again, since the moved event stands.
  const detach = useMutation({
    mutationFn: async () => {
      await operations.officerMoveBoardMeetingInvitations({
        path: { org_ref: orgRef ?? '', meeting_id: meeting.id },
        body: { send_from: 'none' },
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      setOpen(null);
      await refresh(meeting.id);
    },
  });
  // Question 6 (CBG-FIX32): no toast; the line itself shows the new state, as Send's does (B7).
  const told = useMutation({
    mutationFn: async () => {
      await operations.officerRecordBoardDirectorsTold({
        path: { org_ref: orgRef ?? '', meeting_id: meeting.id },
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      await refresh(meeting.id);
    },
    onError: (error) => toast.error(failureLine(error)),
  });

  let line: React.ReactNode = null;
  if (block === null) {
    const defaultSender = senders?.default_send_from;
    if (
      live &&
      senders !== null &&
      (defaultSender === 'my_calendar' ||
        defaultSender === 'board_account' ||
        defaultSender === 'givmo_email')
    ) {
      line = (
        <WithAct
          words={w.notSent}
          act={
            canWrite ? (
              <Act
                loading={send.isPending}
                onClick={() => send.mutate(defaultSender)}
                testId="meeting-invitations-send"
              >
                {w.sendInvitations}
              </Act>
            ) : null
          }
        />
      );
    }
  } else if (block.state === 'detached') {
    line = <span>{w.detached}</span>;
  } else if (block.state === 'needs_attention') {
    const code = block.needs_attention_code ?? '';
    const email = block.organizer_email ?? '';
    if (code === 'organizer_left') {
      const ownerId = boardConnections.data?.find(
        (row) => row.account_email.toLowerCase() === email.toLowerCase(),
      )?.owner_user_id;
      const ownerName =
        (ownerId ? directory.byId.get(ownerId) : undefined) ??
        directory.byEmail.get(email.toLowerCase()) ??
        email;
      line = (
        <WithAct
          attention
          words={attentionLine(block, ownerName)}
          act={
            canWrite && live ? (
              <Act onClick={() => setOpen('move')} testId="meeting-invitations-move">
                {w.moveMeetings}
              </Act>
            ) : null
          }
        />
      );
    } else if (code === 'restored') {
      line = (
        <WithAct
          attention
          words={attentionLine(block)}
          act={
            canWrite ? (
              <Act
                loading={again.isPending}
                onClick={() => again.mutate()}
                testId="meeting-invitations-schedule-again"
              >
                {w.scheduleAgain}
              </Act>
            ) : null
          }
        />
      );
    } else if (code === 'event_missing' || code === 'marker_missing' || RECONNECT_CODES.has(code)) {
      line = (
        <WithAct
          attention
          words={attentionLine(block)}
          act={
            canWrite && live ? (
              <Act onClick={() => setOpen('fix')} testId="meeting-invitations-fix">
                {w.fix}
              </Act>
            ) : null
          }
        />
      );
    } else if (code === 'old_invitation_not_cancelled') {
      const words = attentionLine(block);
      const resend = canWrite && live && linkSender !== null;
      // CBG-FIX37 (review-81's P1): one act at a time. "I've told everyone" pressed while the
      // cancellation is still being sent, or the other way round, raced its settle against the
      // send's outcome, so each waits while the other is pending.
      const directorsTold = (spacing: 'ml-8' | 'none') => (
        <Act
          loading={told.isPending}
          disabled={send.isPending}
          onClick={() => told.mutate()}
          testId="meeting-invitations-directors-told"
          spacing={spacing}
        >
          {w.directorsTold}
        </Act>
      );
      line = (
        <WithAct
          attention
          words={words}
          act={
            canWrite && live && linkSender !== null ? (
              <Act
                loading={send.isPending}
                disabled={told.isPending}
                onClick={() => send.mutate(linkSender)}
                testId="meeting-invitations-send-cancellation"
                spacing="mx-8"
              >
                {w.sendTheCancellation}
              </Act>
            ) : canWrite ? (
              directorsTold('ml-8')
            ) : null
          }
          then={resend ? directorsTold('none') : null}
        />
      );
    } else {
      line = (
        <WithAct
          attention
          words={attentionLine(block)}
          act={
            canWrite && live && linkSender !== null ? (
              <Act
                loading={send.isPending}
                onClick={() => send.mutate(linkSender)}
                testId="meeting-invitations-send-again"
              >
                {w.sendAgainAct}
              </Act>
            ) : null
          }
        />
      );
    }
  } else if (block.state === 'pending' || block.state === 'sent' || block.state === 'cancelled') {
    const count = block.invited_count;
    line = (
      <WithAct
        words={
          block.channel === 'givmo_invitation'
            ? w.sentGivmo(count)
            : w.sentFrom(block.organizer_email ?? '', count)
        }
        act={
          canWrite && live && block.state !== 'cancelled' ? (
            <Act onClick={() => setOpen('send-from-another')} testId="meeting-invitations-change">
              {w.change}
            </Act>
          ) : null
        }
      />
    );
  }
  if (line === null) return null;

  const departed =
    open === 'move' && block !== null
      ? (() => {
          const email = (block.organizer_email ?? '').toLowerCase();
          const row = boardConnections.data?.find(
            (entry) => entry.account_email.toLowerCase() === email,
          );
          const list =
            row !== undefined
              ? meetingsNamed((row.upcoming_meeting_ids ?? []).map(String), meetings.byId)
              : meetingsSentFrom(email, meetings.byId.values());
          // The team list's name for them, or none: the dialog never puts an address in its
          // title (CBG-FIX116).
          const ownerName =
            (row?.owner_user_id ? directory.byId.get(row.owner_user_id) : undefined) ??
            directory.byEmail.get(email) ??
            null;
          return { row, list, ownerName };
        })()
      : null;

  return (
    <>
      {row(
        <>
          <span data-testid="meeting-invitations">{line}</span>
          {(block === null ? [] : quietLines(block, live)).map(({ words, testId }) => (
            <span key={testId} className={`mt-2 block ${SMALL}`} data-testid={testId}>
              {words}
            </span>
          ))}
        </>,
      )}
      {open === 'fix' && block !== null ? (
        <FixDialog
          block={block}
          own={own}
          canSendAgain={linkSender !== null}
          sending={send.isPending}
          sendError={send.error}
          detaching={detach.isPending}
          detachError={detach.error}
          flow={flow}
          onSendAgain={() => linkSender !== null && send.mutate(linkSender)}
          onDetach={() => detach.mutate()}
          onSendFromAnother={() => setOpen('send-from-another')}
          onCancelMeeting={() => setOpen('cancel')}
          onClose={() => setOpen(null)}
        />
      ) : null}
      {open === 'send-from-another' && block !== null ? (
        <SendFromAnotherDialog
          meeting={meeting}
          block={block}
          senders={senders}
          linkSender={linkSender}
          orgName={name}
          onClose={() => setOpen(null)}
        />
      ) : null}
      {open === 'move' && departed !== null && orgRef !== null ? (
        <MoveMeetingsDialog
          orgRef={orgRef}
          account={{
            connection_id: departed.row?.connection_id ?? null,
            account_email: block?.organizer_email ?? '',
            account_kind: 'personal',
          }}
          ownerName={departed.ownerName}
          meetings={departed.list}
          senders={senders}
          orgName={name}
          onClose={() => setOpen(null)}
        />
      ) : null}
      {open === 'cancel' && orgRef !== null ? (
        <CancelMeetingDialog
          orgRef={orgRef}
          meetingId={meeting.id}
          onClose={() => setOpen(null)}
          onCancelled={() => {
            setOpen(null);
            onCancelled();
          }}
        />
      ) : null}
      {open === null && flow.outcome !== null ? (
        <Dialog.Root open onOpenChange={(next) => !next && flow.reset()}>
          <Dialog.Content
            aria-label={w.connectTitle}
            width={448}
            className="flex flex-col gap-16"
            testId="calendar-connect-dialog"
          >
            <ConnectAnswer
              outcome={flow.outcome}
              boardAccount={false}
              onClose={flow.reset}
              onAgain={flow.reset}
            />
          </Dialog.Content>
        </Dialog.Root>
      ) : null}
    </>
  );
}

// ── What needs attention ─────────────────────────────────────────────────────────────────────────

function Buttons({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-wrap justify-end gap-10">{children}</div>;
}

function FixDialog({
  block,
  own,
  canSendAgain,
  sending,
  sendError,
  detaching,
  detachError,
  flow,
  onSendAgain,
  onDetach,
  onSendFromAnother,
  onCancelMeeting,
  onClose,
}: {
  block: MeetingCalendar;
  own: CalendarConnection | undefined;
  canSendAgain: boolean;
  sending: boolean;
  sendError: unknown;
  detaching: boolean;
  detachError: unknown;
  flow: ReturnType<typeof useCalendarConnect>;
  onSendAgain: () => void;
  onDetach: () => void;
  onSendFromAnother: () => void;
  onCancelMeeting: () => void;
  onClose: () => void;
}) {
  const { orgRef } = useBoardHost();
  const directory = useOfficerDirectory(orgRef, own === undefined);
  const email = block.organizer_email ?? '';
  const provider = providerName(block.provider);

  if (block.needs_attention_code === 'marker_missing') {
    // CBG-FIX181: moved to another calendar. No Send again (a second meeting while the moved one
    // stands), no other calendar (the same), no Cancel here (it wouldn't reach the moved event).
    const title = w.attention.marker_missing(provider);
    return (
      <Dialog.Root open onOpenChange={(next) => !next && onClose()}>
        <Dialog.Content
          aria-label={title}
          width={448}
          className="flex flex-col gap-16"
          testId="calendar-fix-dialog"
        >
          <div className="flex flex-col gap-6">
            <Dialog.Title className={DIALOG_TITLE}>{title}</Dialog.Title>
            <p className={BODY}>{w.fixMovedBody(provider)}</p>
            <p className={SMALL}>{w.sendFromAnotherDetachNote(provider)}</p>
          </div>
          {detachError ? (
            <ErrorMessage testId="calendar-fix-error">{failureLine(detachError)}</ErrorMessage>
          ) : null}
          <Buttons>
            <Button
              appearance="saas"
              tone="secondary"
              onClick={onClose}
              testId="calendar-fix-close"
            >
              {w.close}
            </Button>
            <Button
              appearance="saas"
              tone="primary"
              loading={detaching}
              onClick={onDetach}
              testId="calendar-fix-detach"
            >
              {w.fixMovedDetach}
            </Button>
          </Buttons>
        </Dialog.Content>
      </Dialog.Root>
    );
  }

  if (block.needs_attention_code === 'event_missing') {
    const title = w.fixMissingTitle(provider);
    return (
      <Dialog.Root open onOpenChange={(next) => !next && onClose()}>
        <Dialog.Content
          aria-label={title}
          width={448}
          className="flex flex-col gap-16"
          testId="calendar-fix-dialog"
        >
          <div className="flex flex-col gap-6">
            <Dialog.Title className={DIALOG_TITLE}>{title}</Dialog.Title>
            <p className={BODY}>{w.fixMissingBody(email)}</p>
            {canSendAgain ? <p className={SMALL}>{w.sendAgainNote}</p> : null}
          </div>
          {sendError ? (
            <ErrorMessage testId="calendar-fix-error">{failureLine(sendError)}</ErrorMessage>
          ) : null}
          <Buttons>
            <Button
              appearance="saas"
              tone="ghost"
              className="text-danger-600"
              onClick={onCancelMeeting}
              testId="calendar-fix-cancel-meeting"
            >
              {w.cancelMeeting}
            </Button>
            <Button
              appearance="saas"
              tone="secondary"
              onClick={onClose}
              testId="calendar-fix-close"
            >
              {w.close}
            </Button>
            {canSendAgain ? (
              <Button
                appearance="saas"
                tone="primary"
                loading={sending}
                onClick={onSendAgain}
                testId="calendar-fix-send-again"
              >
                {w.sendAgain}
              </Button>
            ) : null}
          </Buttons>
        </Dialog.Content>
      </Dialog.Root>
    );
  }

  const mine = own !== undefined;
  const ownerName = directory.byEmail.get(email.toLowerCase()) ?? email;
  const title = mine ? w.fixReconnectTitleOwner : w.fixReconnectTitleOthers;
  return (
    <Dialog.Root open onOpenChange={(next) => !next && onClose()}>
      <Dialog.Content
        aria-label={title}
        width={448}
        className="flex flex-col gap-16"
        testId="calendar-fix-dialog"
      >
        <div className="flex flex-col gap-6">
          <Dialog.Title className={DIALOG_TITLE}>{title}</Dialog.Title>
          <p className={BODY}>{w.fixReconnectBody(email)}</p>
          {mine ? null : <p className={SMALL}>{w.fixReconnectOthersNote(ownerName)}</p>}
        </div>
        <Buttons>
          <Button
            appearance="saas"
            tone="secondary"
            onClick={onSendFromAnother}
            testId="calendar-fix-send-from-another"
          >
            {w.sendFromAnother}
          </Button>
          {mine ? (
            <Button
              appearance="saas"
              tone="primary"
              loading={flow.starting}
              onClick={() => {
                flow.begin({
                  accountKind: 'personal',
                  reconnectConnectionId: own.connection_id,
                  loginHint: own.account_email,
                });
                onClose();
              }}
              testId="calendar-fix-reconnect"
            >
              {w.reconnect}
            </Button>
          ) : null}
        </Buttons>
      </Dialog.Content>
    </Dialog.Root>
  );
}

// ── One meeting, from another calendar ───────────────────────────────────────────────────────────

function SendFromAnotherDialog({
  meeting,
  block,
  senders,
  linkSender,
  orgName,
  onClose,
}: {
  meeting: OrgBoardMeetingOut;
  block: MeetingCalendar;
  senders: BoardCalendarSendersOut | null;
  linkSender: LinkSender | null;
  orgName: string;
  onClose: () => void;
}) {
  const { orgRef } = useBoardHost();
  const operations = useBoardOperations();
  const refresh = useRefreshCalendar(orgRef);
  const provider = providerName(block.provider);
  const organizer = (block.organizer_email ?? '').toLowerCase();
  // Every other sender this officer is offered (never the meeting's own), then stopping.
  const others = (senders?.senders ?? []).filter(
    (sender) =>
      sender.send_from !== linkSender &&
      !(
        sender.send_from !== 'givmo_email' &&
        (sender.organizer_email ?? '').toLowerCase() === organizer
      ),
  );
  const options = [
    ...others.map((sender) => sender.send_from),
    ...(block.channel === 'org_calendar' ? ['none'] : []),
  ];
  const [to, setTo] = useState<string>(options[0] ?? 'none');
  const move = useMutation({
    mutationFn: async () => {
      const sender = others.find((row) => row.send_from === to);
      await operations.officerMoveBoardMeetingInvitations({
        path: { org_ref: orgRef ?? '', meeting_id: meeting.id },
        body: {
          send_from: to as 'my_calendar' | 'board_account' | 'givmo_email' | 'none',
          ...(sender?.connection_id ? { connection_id: sender.connection_id } : {}),
        },
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      await refresh(meeting.id);
      onClose();
    },
  });
  const body =
    block.channel === 'givmo_invitation'
      ? w.sendFromAnotherBodyGivmo(orgName)
      : w.sendFromAnotherBody(block.organizer_email ?? '');
  return (
    <Dialog.Root open onOpenChange={(next) => !next && onClose()}>
      <Dialog.Content
        aria-label={w.sendFromAnotherTitle}
        width={480}
        className="flex flex-col gap-16"
        testId="calendar-send-from-another-dialog"
      >
        <div className="flex flex-col gap-6">
          <Dialog.Title className={DIALOG_TITLE}>{w.sendFromAnotherTitle}</Dialog.Title>
          <p className={BODY}>{body}</p>
        </div>
        <RadioTileGroup
          value={to}
          onValueChange={setTo}
          aria-label={w.sendFromAnotherTitle}
          testId="calendar-send-from-another-to"
        >
          {others.map((sender) =>
            sender.send_from === 'givmo_email' ? (
              <RadioTile
                key="givmo_email"
                value="givmo_email"
                label={w.pickGivmo}
                description={w.sendFromAnotherGivmoNote(orgName)}
              />
            ) : (
              <RadioTile
                key={sender.send_from}
                value={sender.send_from}
                label={`${sender.send_from === 'my_calendar' ? w.pickMine : w.pickBoard} · ${
                  sender.organizer_email ?? ''
                }`}
                description={w.sendFromAnotherMoveNote}
              />
            ),
          )}
          {block.channel === 'org_calendar' ? (
            <RadioTile
              value="none"
              label={w.sendFromAnotherDetach}
              description={w.sendFromAnotherDetachNote(provider)}
            />
          ) : null}
        </RadioTileGroup>
        {move.isError ? (
          <ErrorMessage testId="calendar-send-from-another-error">
            {failureLine(move.error)}
          </ErrorMessage>
        ) : null}
        <Buttons>
          <Button
            appearance="saas"
            tone="secondary"
            onClick={onClose}
            testId="calendar-send-from-another-cancel"
          >
            {w.cancel}
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={move.isPending}
            disabled={options.length === 0}
            onClick={() => move.mutate()}
            testId="calendar-send-from-another-confirm"
          >
            {w.sendFromButton[to] ?? w.sendFromButton.none}
          </Button>
        </Buttons>
      </Dialog.Content>
    </Dialog.Root>
  );
}
